// src/Pages/Attendance.jsx
import React, { useState, useMemo, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  Grid,
  MenuItem,
  Select,
  FormControl,
  LinearProgress,
  Chip,
  Container,
  Fade,
  alpha,
} from "@mui/material";
import {
  EventAvailable,
  CheckCircle,
  Cancel,
  WbSunny,
  Hotel,
} from "@mui/icons-material";
import apiEndpoints from "../apiconfig";

// ---------------- MOCK DATA FOR 3 MONTHS ----------------
const weekdayLabels = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

// Map status -> colors / label
const statusStyles = {
  present: {
    label: "Present",
    bg: (theme) => alpha("#4CAF50", 0.12),
    border: (theme) => alpha("#4CAF50", 0.5),
    color: "#2E7D32",
    icon: <CheckCircle sx={{ fontSize: 16, ml: 0.5 }} />,
  },
  absent: {
    label: "Absent",
    bg: (theme) => alpha("#E53935", 0.12),
    border: (theme) => alpha("#E53935", 0.5),
    color: "#C62828",
    icon: <Cancel sx={{ fontSize: 16, ml: 0.5 }} />,
  },
  holiday: {
    label: "Holiday",
    bg: (theme) => alpha("#AED581", 0.18),
    border: (theme) => alpha("#8BC34A", 0.7),
    color: "#558B2F",
    icon: <WbSunny sx={{ fontSize: 16, ml: 0.5 }} />,
  },
  leave: {
    label: "Leave",
    bg: (theme) => alpha("#64B5F6", 0.18),
    border: (theme) => alpha("#42A5F5", 0.7),
    color: "#1565C0",
    icon: <Hotel sx={{ fontSize: 16, ml: 0.5 }} />,
  },
  future: {
    label: "",
    bg: (theme) => "rgba(255,255,255,0.6)",
    border: (theme) => "rgba(0,0,0,0.06)",
    color: "#9E9E9E",
    icon: null,
  },
};

const Attendance = () => {
  const [months, setMonths] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [current, setCurrent] = useState(null);
  const year = current?.year;
  const monthIndex = current?.monthIndex;
  const stats = current?.stats;
  const attendancePercentage =
    stats && stats.totalDays
      ? Math.round((stats.present / stats.totalDays) * 100)
      : 0;
  const isLoading = !current || !stats;

  // ---------- Build calendar grid ----------
  const { cells } = useMemo(() => {
    if (!year && year !== 0) return { cells: [] };

    const firstDayJs = new Date(year, monthIndex, 1).getDay();
    const firstDayIndex = (firstDayJs + 6) % 7;
    const daysInMonth = new Date(year, monthIndex + 1, 0).getDate();

    const temp = [];
    const totalCells = firstDayIndex + daysInMonth;

    for (let i = 0; i < totalCells; i++) {
      temp.push(i < firstDayIndex ? null : i - firstDayIndex + 1);
    }

    return { cells: temp };
  }, [year, monthIndex]);

  useEffect(() => {
    const studentGuid =
      JSON.parse(localStorage.getItem("active_student") || "{}").guid;


    fetch(
      `${apiEndpoints.attendance}?action=months&student_guid=${studentGuid}`
    )
      .then((res) => res.json())
      .then((res) => {
        if (res.status && res.months.length) {
          setMonths(res.months);
          setSelectedMonth(res.months[res.months.length - 1]); // latest month
        }
      });
  }, []);

  useEffect(() => {
    if (!selectedMonth) return;

    const studentGuid =
      JSON.parse(localStorage.getItem("active_student") || "{}").guid


    fetch(
      `${apiEndpoints.attendance}?action=month&student_guid=${studentGuid}&year=${selectedMonth.year}&month=${selectedMonth.month}`
    )
      .then((res) => res.json())
      .then((res) => {
        if (res.status) {
          setCurrent(res.data);
        }
      });
  }, [selectedMonth]);

  return (
    <Box
      sx={{
        minHeight: "100vh",
        position: "relative",
        overflow: "hidden",
        backgroundImage: `
          linear-gradient(rgba(255, 255, 255, 0.92), rgba(255, 255, 255, 0.92)),
          url('https://images.unsplash.com/photo-1503676260728-1c00da094a0b?auto=format&fit=crop&w=1920')
        `,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundAttachment: "fixed",
        "&::before": {
          content: '""',
          position: "absolute",
          inset: 0,
          background: `
            radial-gradient(circle at 20% 80%, rgba(120, 119, 198, 0.1) 0%, transparent 50%),
            radial-gradient(circle at 80% 20%, rgba(255, 119, 198, 0.1) 0%, transparent 50%)
          `,
          pointerEvents: "none",
        },
      }}
    >
      <Container
        maxWidth="xl"
        sx={{ position: "relative", zIndex: 1, px: { xs: 1.5, sm: 2, md: 3 } }}
      >
        {/* HEADER + FILTER */}
        {!current && (
          <Box
            sx={{
              minHeight: "100vh", // full screen height
              display: "flex",
              alignItems: "center", // vertical center
              justifyContent: "center", // horizontal center
              textAlign: "center",
              color: "text.secondary",
              px: 2,
            }}
          >
            <Typography variant="h6" color="text.secondary">
              Loading attendance...
            </Typography>
          </Box>
        )}

        {current && stats && (
          <>
            <Fade in timeout={700}>
              <Box
                sx={{
                  py: 4,
                  px: { xs: 2, md: 4 },
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: { xs: "flex-start", md: "center" },
                  gap: 2,
                  flexWrap: "wrap",
                }}
              >
                <Box>
                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: 700,
                      color: "#333",
                      mb: 1,
                      fontSize: { xs: "1.7rem", md: "2.1rem" },
                    }}
                  >
                    Attendance History
                  </Typography>
                  <Typography sx={{ color: "text.secondary" }}>
                    Review daily presence and patterns across months.
                  </Typography>
                </Box>

                <FormControl
                  sx={{
                    minWidth: { xs: "100%", sm: 220 },
                  }}
                >
                  <Select
                    value={selectedMonth?.label || ""}
                    onChange={(e) => {
                      const m = months.find((x) => x.label === e.target.value);
                      setSelectedMonth(m);
                    }}
                    sx={{
                      bgcolor: "rgba(255,255,255,0.9)",
                      borderRadius: "16px",
                      px: 1.5,
                      boxShadow: "0 6px 18px rgba(15, 23, 42, 0.12)",
                    }}
                  >
                    {months.map((m) => (
                      <MenuItem key={m.label} value={m.label}>
                        {m.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            </Fade>

            {/* STATS ROW (glass cards like dashboard) */}
            <Fade in timeout={900}>
              <Grid
                container
                spacing={{ xs: 2, sm: 3 }}
                sx={{ mb: 4, px: { xs: 1, sm: 2, md: 4 } }}
                alignItems="stretch"
              >
                <Grid item sx={{
                width: {
                  xs: "100%",
                  sm: "50%",
                  md: "50%",
                  lg: "25%",
                  xl: "25%",
                },
              }}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 3,
                      borderRadius: "18px",
                      background: "rgba(255,255,255,0.7)",
                      backdropFilter: "blur(16px)",
                      border: "1px solid rgba(255,255,255,0.5)",
                      boxShadow: "0 10px 25px rgba(15, 23, 42, 0.12)",
                      height: "100%",
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{ color: "#666", fontWeight: 500, mb: 1 }}
                    >
                      Present Days
                    </Typography>
                    <Typography
                      variant="h4"
                      sx={{ color: "#2E7D32", fontWeight: 700 }}
                    >
                      {stats && stats.present}
                    </Typography>
                    <Typography variant="caption" sx={{ color: "#888" }}>
                      out of {stats.totalDays} school days
                    </Typography>
                    <LinearProgress
                      variant="determinate"
                      value={attendancePercentage}
                      sx={{
                        mt: 2,
                        height: 6,
                        borderRadius: 3,
                        bgcolor: alpha("#4CAF50", 0.1),
                        "& .MuiLinearProgress-bar": {
                          borderRadius: 3,
                          bgcolor: "#4CAF50",
                        },
                      }}
                    />
                  </Paper>
                </Grid>

                <Grid item sx={{
                width: {
                  xs: "100%",
                  sm: "50%",
                  md: "50%",
                  lg: "25%",
                  xl: "25%",
                },
              }}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 3,
                      borderRadius: "18px",
                      background: "rgba(255,255,255,0.7)",
                      backdropFilter: "blur(16px)",
                      border: "1px solid rgba(255,255,255,0.5)",
                      boxShadow: "0 10px 25px rgba(15, 23, 42, 0.12)",
                      height: "100%",
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{ color: "#666", fontWeight: 500, mb: 1 }}
                    >
                      Absent Days
                    </Typography>
                    <Typography
                      variant="h4"
                      sx={{ color: "#C62828", fontWeight: 700 }}
                    >
                      {stats.absent}
                    </Typography>
                    <Typography variant="caption" sx={{ color: "#888" }}>
                      {((stats.absent / stats.totalDays) * 100).toFixed(1)}% of
                      month
                    </Typography>
                    <LinearProgress
                      variant="determinate"
                      value={(stats.absent / stats.totalDays) * 100}
                      sx={{
                        mt: 2,
                        height: 6,
                        borderRadius: 3,
                        bgcolor: alpha("#E53935", 0.1),
                        "& .MuiLinearProgress-bar": {
                          borderRadius: 3,
                          bgcolor: "#E53935",
                        },
                      }}
                    />
                  </Paper>
                </Grid>

                <Grid item sx={{
                width: {
                  xs: "100%",
                  sm: "50%",
                  md: "50%",
                  lg: "25%",
                  xl: "25%",
                },
              }}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 3,
                      borderRadius: "18px",
                      background: "rgba(255,255,255,0.7)",
                      backdropFilter: "blur(16px)",
                      border: "1px solid rgba(255,255,255,0.5)",
                      boxShadow: "0 10px 25px rgba(15, 23, 42, 0.12)",
                      height: "100%",
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{ color: "#666", fontWeight: 500, mb: 1 }}
                    >
                      Attendance %
                    </Typography>
                    <Typography variant="h4" sx={{ fontWeight: 700 }}>
                      {attendancePercentage}%
                    </Typography>
                    <Chip
                      label={
                        attendancePercentage >= 90
                          ? "Great consistency!"
                          : "Can be improved"
                      }
                      size="small"
                      sx={{
                        mt: 1,
                        bgcolor: alpha("#1976D2", 0.08),
                        color: "#1976D2",
                        fontWeight: 500,
                      }}
                    />
                  </Paper>
                </Grid>

                <Grid item sx={{
                width: {
                  xs: "100%",
                  sm: "50%",
                  md: "50%",
                  lg: "25%",
                  xl: "25%",
                },
              }}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 3,
                      borderRadius: "18px",
                      background: "rgba(255,255,255,0.7)",
                      backdropFilter: "blur(16px)",
                      border: "1px solid rgba(255,255,255,0.5)",
                      boxShadow: "0 10px 25px rgba(15, 23, 42, 0.12)",
                      height: "100%",
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{ color: "#666", fontWeight: 500, mb: 1 }}
                    >
                      Current Streak
                    </Typography>
                    <Typography
                      variant="h4"
                      sx={{ fontWeight: 700, color: "#FF8F00" }}
                    >
                      {stats.streak} days
                    </Typography>
                    <Typography variant="caption" sx={{ color: "#888" }}>
                      Consecutive present days
                    </Typography>
                    <Box sx={{ mt: 2, display: "flex", gap: 0.5 }}>
                      {[...Array(Math.min(stats.streak, 7))].map((_, i) => (
                        <Box
                          key={i}
                          sx={{
                            flex: 1,
                            height: 6,
                            borderRadius: 999,
                            bgcolor: alpha("#FFB300", i === 0 ? 0.35 : 0.15),
                          }}
                        />
                      ))}
                    </Box>
                  </Paper>
                </Grid>
              </Grid>
            </Fade>

            {/* CALENDAR */}
            <Fade in timeout={1100}>
              <Box
                sx={{
                  px: { xs: 2, md: 4 },
                  pb: 6,
                }}
              >
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    borderRadius: "22px",
                    background: "rgba(255,255,255,0.7)",
                    backdropFilter: "blur(16px)",
                    border: "1px solid rgba(255,255,255,0.5)",
                    boxShadow: "0 10px 30px rgba(15, 23, 42, 0.12)",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: { xs: "column", sm: "row" },
                      justifyContent: "space-between",
                      alignItems: { xs: "flex-start", sm: "center" },
                      gap: { xs: 1.5, sm: 0 },
                      mb: 2.5,
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 600,
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      <EventAvailable /> Calendar View
                    </Typography>
                    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                      <Chip
                        size="small"
                        label="Present"
                        sx={{
                          bgcolor: alpha("#4CAF50", 0.12),
                          color: "#2E7D32",
                        }}
                      />
                      <Chip
                        size="small"
                        label="Absent"
                        sx={{
                          bgcolor: alpha("#E53935", 0.12),
                          color: "#C62828",
                        }}
                      />
                      <Chip
                        size="small"
                        label="Holiday"
                        sx={{
                          bgcolor: alpha("#8BC34A", 0.14),
                          color: "#558B2F",
                        }}
                      />
                    </Box>
                  </Box>

                  {/* Weekday Header */}
                  <Grid
                    container
                    columns={{ xs: 4, sm: 7 }} // 🔥 match calendar
                    sx={{ mb: 1 }}
                  >
                    {weekdayLabels.map((w) => (
                      <Grid item xs={1} key={w}>
                        <Box
                          sx={{
                            textAlign: "center",
                            py: 1,
                            fontSize: 13,
                            fontWeight: 600,
                            color: "#9e9e9e",
                            textTransform: "uppercase",
                          }}
                        >
                          {w}
                        </Box>
                      </Grid>
                    ))}
                  </Grid>

                  {/* Days Grid */}
                  <Grid
                    container
                    columns={{ xs: 4, sm: 7 }} // 🔥 key change
                    spacing={{ xs: 1, sm: 1.2 }}
                  >
                    {cells.map((date, idx) => {
                      if (!date) {
                        // empty pad cell
                        return (
                          <Grid item xs={1} key={`empty-${idx}`}>
                            <Box
                              sx={{
                                borderRadius: "14px",
                                minHeight: 70,
                                bgcolor: "rgba(248,248,248,0.6)",
                                border: "1px dashed rgba(0,0,0,0.04)",
                              }}
                            />
                          </Grid>
                        );
                      }

                      const statusKey = current.days[date] || "future";
                      const style = statusStyles[statusKey];

                      return (
                        <Grid
                          item
                          xs={1}
                          sx={{
                            minWidth: { xs: 44, sm: "auto" }, // 🔥 critical for 320px
                          }}
                          key={idx}
                        >
                          <Box
                            sx={{
                              borderRadius: "14px",
                              minHeight: { xs: 56, sm: 72, md: 90 },
                              p: { xs: 0.6, sm: 1.2 },
                              display: "flex",
                              flexDirection: "column",
                              justifyContent: "space-between",
                              bgcolor: (theme) => style.bg(theme),
                              border: (theme) =>
                                `1px solid ${style.border(theme)}`,
                              color: style.color,
                              transition: "all 0.15s ease",
                              cursor:
                                statusKey === "future" ? "default" : "pointer",
                              "&:hover": {
                                transform:
                                  statusKey === "future"
                                    ? "none"
                                    : "translateY(-2px)",
                                boxShadow:
                                  statusKey === "future"
                                    ? "none"
                                    : "0 8px 18px rgba(15,23,42,0.18)",
                                bgcolor:
                                  statusKey === "future"
                                    ? "rgba(255,255,255,0.8)"
                                    : "rgba(255,255,255,0.95)",
                              },
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "flex-start",
                              }}
                            >
                              <Typography
                                sx={{
                                  fontWeight: 700,
                                  fontSize: { xs: 13, sm: 16 },
                                  color:
                                    statusKey === "future"
                                      ? "#616161"
                                      : style.color,
                                }}
                              >
                                {date}
                              </Typography>
                              {statusKey !== "future" && (
                                <Box
                                  sx={{
                                    width: { xs: 6, sm: 8 },
                                    height: { xs: 6, sm: 8 },

                                    borderRadius: "50%",
                                    bgcolor:
                                      statusKey === "present"
                                        ? "#4CAF50"
                                        : statusKey === "absent"
                                        ? "#E53935"
                                        : "#8BC34A",
                                  }}
                                />
                              )}
                            </Box>

                            {statusKey !== "future" && (
                              <Box sx={{ mt: 1 }}>
                                <Chip
                                  label={style.label}
                                  size="small"
                                  icon={style.icon}
                                  sx={{
                                    height: { xs: 18, sm: 22 },
                                    maxWidth: "100%",
                                    borderRadius: "999px",
                                    fontSize: { xs: 0, sm: 11 }, // 🔥 hide text on mobile
                                    bgcolor: "rgba(255,255,255,0.9)",
                                    color: style.color,
                                    fontWeight: 600,
                                    px: { xs: 0.5, sm: 0 },
                                    "& .MuiChip-label": {
                                      px: { sm: 1 },
                                      display: { xs: "none", sm: "block" }, // 🔥 key line
                                    },
                                    "& .MuiChip-icon": {
                                      ml: 0,
                                      mr: 0,
                                      fontSize: 14,
                                    },
                                  }}
                                />
                              </Box>
                            )}
                          </Box>
                        </Grid>
                      );
                    })}
                  </Grid>
                </Paper>
              </Box>
            </Fade>
          </>
        )}
      </Container>
    </Box>
  );
};

export default Attendance;
